import type {
  MentorProfileChangeRequest,
  MentorProfileDraft,
  MentorProfileReview,
} from "@/lib/api/mentor-profile";
import { openChangeRequests } from "@/lib/mentor-profile/submit-readiness";
import {
  SECTION_TO_BUILD_STEP,
  WIZARD_BUILD_STEPS,
  buildStepIndexFromFurthest,
  isReviewSection,
  type OnboardingPhase,
  type WizardBuildStep,
} from "@/lib/mentor-profile/wizard-steps";

export type OnboardingEntry = {
  phase: OnboardingPhase;
  buildStepIndex: number;
  baselineDraft: MentorProfileDraft | null;
  openRequests: MentorProfileChangeRequest[];
};

export function flaggedBuildIndices(
  requests: MentorProfileChangeRequest[],
): number[] {
  const indices = new Set<number>();
  for (const request of openChangeRequests(requests)) {
    if (!isReviewSection(request.section)) continue;
    const step = SECTION_TO_BUILD_STEP[request.section];
    const index = WIZARD_BUILD_STEPS.indexOf(step);
    if (index >= 0) indices.add(index);
  }
  return [...indices].sort((a, b) => a - b);
}

export function firstFlaggedBuildIndex(
  requests: MentorProfileChangeRequest[],
): number {
  return flaggedBuildIndices(requests)[0] ?? 0;
}

export function firstFlaggedBuildStep(
  requests: MentorProfileChangeRequest[],
): WizardBuildStep | null {
  const index = flaggedBuildIndices(requests)[0];
  return index === undefined ? null : (WIZARD_BUILD_STEPS[index] ?? null);
}

export function notesForStep(
  requests: MentorProfileChangeRequest[],
  step: WizardBuildStep,
) {
  return requests
    .filter(
      (request) =>
        isReviewSection(request.section) &&
        SECTION_TO_BUILD_STEP[request.section] === step,
    )
    .map((request) => request.note);
}

export function resolveOnboardingEntry(
  review: MentorProfileReview,
): OnboardingEntry {
  const openRequests = openChangeRequests(review.change_requests);
  const baselineDraft =
    review.status === "changes_requested" || openRequests.length > 0
      ? structuredClone(review.draft)
      : null;

  if (review.status === "submitted" || review.status === "in_review") {
    return {
      phase: "submitted",
      buildStepIndex: 0,
      baselineDraft,
      openRequests,
    };
  }

  if (
    (review.status === "changes_requested" ||
      review.status === "in_progress") &&
    openRequests.length > 0
  ) {
    return {
      phase: "revision_feedback",
      buildStepIndex: firstFlaggedBuildIndex(openRequests),
      baselineDraft,
      openRequests,
    };
  }

  if (review.status === "changes_requested") {
    return {
      phase: "review",
      buildStepIndex: buildStepIndexFromFurthest(review.furthest_step),
      baselineDraft,
      openRequests,
    };
  }

  if (review.status === "approved" || review.published_snapshot) {
    return {
      phase: "approved",
      buildStepIndex: 0,
      baselineDraft,
      openRequests,
    };
  }

  const furthest = review.furthest_step;
  if (furthest === "review") {
    return {
      phase: "review",
      buildStepIndex: buildStepIndexFromFurthest(furthest),
      baselineDraft,
      openRequests,
    };
  }
  if (furthest === "intro") {
    return {
      phase: "intro",
      buildStepIndex: 0,
      baselineDraft,
      openRequests,
    };
  }
  if (
    furthest &&
    WIZARD_BUILD_STEPS.includes(furthest as WizardBuildStep)
  ) {
    return {
      phase: "build",
      buildStepIndex: buildStepIndexFromFurthest(furthest),
      baselineDraft,
      openRequests,
    };
  }

  return {
    phase: "confirm",
    buildStepIndex: 0,
    baselineDraft,
    openRequests,
  };
}
